// Phase 8.1 — Modern login page.
//
// Layout: full-viewport soft background, centered ~420px card.
// Priority order matches the user's ask:
//   1. Google (functional, primary CTA)
//   2. Microsoft (visual "Coming soon", disabled)
//   3. Apple (visual "Coming soon", disabled)
//   4. Divider
//   5. Email + password (fallback, still fully functional)
//
// Error banners read from the URL query param (?error=…) — populated
// by `AuthCallback` when the Google exchange fails. Kept small and
// declarative so future providers can be added by touching only the
// `PROVIDER_BUTTONS` config.

import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/lib/auth";

// Where to send the user after auth. Falls back to landing.
function useNextPath() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const raw = params.get("next");
  if (!raw) return "/";
  if (raw.startsWith("http") || raw.startsWith("//")) return "/";
  return raw;
}

// Read the ?error= query param → user-facing message. Keeping the
// map here (not on the server) means the server can stay generic
// with its detail codes.
function useErrorMessage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const code = params.get("error");
  if (!code) return null;
  if (code === "domain_not_allowed") {
    return "Only @gostops.com Google accounts can sign in.";
  }
  if (code === "oauth_failed") {
    return "Google sign-in failed. Please try again or use email + password.";
  }
  return "Something went wrong. Please try again.";
}

// Google's official brand mark (multicolour "G"). Inline SVG so we
// don't ship a font/asset. Playbook: use the real logo, not a
// hand-drawn approximation.
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.85c-.21 1.13-.84 2.09-1.79 2.73v2.26h2.9c1.69-1.56 2.68-3.86 2.68-6.63z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.83.86-3.06.86-2.35 0-4.35-1.59-5.06-3.73H.96v2.34C2.44 15.98 5.48 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.94 10.69c-.18-.54-.28-1.12-.28-1.69s.1-1.15.28-1.69V4.97H.96C.35 6.19 0 7.55 0 9c0 1.45.35 2.81.96 4.03l2.98-2.34z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.97l2.98 2.34C4.65 5.17 6.65 3.58 9 3.58z"
      />
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8" height="8" fill="#F25022" />
      <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
      <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
      <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 384 512" aria-hidden="true">
      <path
        fill="currentColor"
        d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
      />
    </svg>
  );
}


export default function Login() {
  const { user, login } = useAuth();
  const nextPath = useNextPath();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Already logged in? Skip the form.
  if (user && typeof user === "object") {
    return <Navigate to={nextPath} replace />;
  }

  const onGoogleClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError("");
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setFormError("Invalid email or password.");
      } else {
        setFormError(
          err?.response?.data?.detail || err?.message || "Sign-in failed. Try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px] space-y-6">
        {/* Brand block */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-1.5 select-none">
            <span
              className="h-6 w-6 rounded-sm bg-foreground"
              aria-hidden="true"
            />
            <span className="text-xl font-semibold tracking-tight text-foreground">
              goStops
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Sign in to Procurement Module
          </h1>
          <p className="text-sm text-muted-foreground">
            Choose a provider to continue.
          </p>
        </div>

        {/* Sign-in card */}
        <div className="rounded-xl border border-border/60 bg-card shadow-sm p-6 space-y-4">
          {errorMessage && (
            <Alert variant="destructive" data-testid="login-error-banner">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Provider buttons */}
          <div className="space-y-2">
            <button
              type="button"
              onClick={onGoogleClick}
              data-testid="login-google-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border/70 bg-white hover:bg-slate-50 text-slate-900 h-10 px-4 text-sm font-medium transition-colors"
            >
              <GoogleLogo />
              Continue with Google
            </button>
            <button
              type="button"
              disabled
              data-testid="login-microsoft-button"
              aria-disabled="true"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border/50 bg-muted/30 text-muted-foreground h-10 px-4 text-sm font-medium cursor-not-allowed"
            >
              <MicrosoftLogo />
              <span>Continue with Microsoft</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/80 border border-muted-foreground/30 rounded px-1.5 py-0.5">
                Coming soon
              </span>
            </button>
            <button
              type="button"
              disabled
              data-testid="login-apple-button"
              aria-disabled="true"
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border/50 bg-muted/30 text-muted-foreground h-10 px-4 text-sm font-medium cursor-not-allowed"
            >
              <AppleLogo />
              <span>Continue with Apple</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/80 border border-muted-foreground/30 rounded px-1.5 py-0.5">
                Coming soon
              </span>
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="h-px bg-border flex-1" />
            <span>or continue with email</span>
            <span className="h-px bg-border flex-1" />
          </div>

          {/* Email + password */}
          <form onSubmit={onSubmit} className="space-y-3" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email" className="text-xs">
                Email
              </Label>
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@gostops.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password" className="text-xs">
                Password
              </Label>
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            {formError && (
              <div
                className="text-xs text-destructive"
                role="alert"
                data-testid="login-form-error"
              >
                {formError}
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Only @gostops.com accounts are allowed.
        </p>
      </div>
    </div>
  );
}
